import React, { useState, useEffect } from 'react';
import { Typography, Button } from '@mui/material';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { EditCoveragePlan } from '../EditCoveragePlan';
import styled from 'styled-components';
import type { BranchCoverageArea, CoverageArea } from '../../types/coverage-area';
import type { Election } from '../../app/feathers/elections/Election';
import type { ElectionImplementation } from '../../types/elections/ElectionImplementation';

export interface EditBranchCoveragePlanProps {
  election: string;
  onSave?: (result: ElectionImplementation) => void | Promise<void>;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Wrapper that loads branchCoverageArea from election-implementations and saves via patch on Save.
 * EditCoveragePlan is controlled (value/onChange); persistence is done here when user clicks Save.
 */
const EditBranchCoveragePlan = ({
  election: electionKey,
  onSave,
  style,
  className
}: EditBranchCoveragePlanProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [branchCoverageArea, setBranchCoverageArea] = useState<BranchCoverageArea | null>(null);
  const [election, setElection] = useState<Election | null>(null);
  const [stateId, setStateId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!feathers || !electionKey) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [eiRes, electionRes] = await Promise.all([
          feathers.getService('election-implementations').get(electionKey).catch(() => null),
          feathers.getService('elections').get(electionKey)
        ]);
        if (cancelled) return;
        type ElectionImplementationResponse = { branchCoverageArea?: BranchCoverageArea };
        setBranchCoverageArea((eiRes as ElectionImplementationResponse | null)?.branchCoverageArea ?? null);
        setElection(electionRes as Election);
        setStateId((electionRes as Election)?.state ?? null);
      } catch (err) {
        if (!cancelled) setError((err as Error)?.message ?? 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [feathers, electionKey]);

  const handleChange = (next: CoverageArea) => {
    setBranchCoverageArea(next);
  };

  const handleSave = async () => {
    const next: BranchCoverageArea = branchCoverageArea ?? {
      coveragePlan: 'auto-coverage',
      supportedAreas: [],
      supportedDistrictTypes: [],
      priorityArea: []
    };
    const hasSupportedAreas = (next.supportedAreas?.length ?? 0) > 0;
    const hasSupportedDistrictTypes = (next.supportedDistrictTypes?.length ?? 0) > 0;
    const normalizedNext: BranchCoverageArea = (!hasSupportedAreas && !hasSupportedDistrictTypes)
      ? {
        ...next,
        priorityArea: []
      }
      : next;
    setSaving(true);
    try {
      const result = await feathers.getService('election-implementations').patch(electionKey, { branchCoverageArea: normalizedNext });
      if (onSave && typeof onSave === 'function') {
        await Promise.resolve(onSave(result));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={style} className={className}>
        <Typography variant='body1'>Loading...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={style} className={className}>
        <Typography variant='body1' color='error'>Error loading election implementation</Typography>
      </div>
    );
  }

  if (!stateId) {
    return (
      <div style={style} className={className}>
        <Typography variant='body1' color='error'>Election state is required</Typography>
      </div>
    );
  }

  const defaultValue: CoverageArea = {
    coveragePlan: 'auto-coverage',
    supportedAreas: [],
    supportedDistrictTypes: [],
    priorityArea: []
  };
  const currentValue = branchCoverageArea ?? defaultValue;
  const submittable = true; // Allow submitting empty branchCoverageArea (default) or any configured coverage

  return (
    <Wrapper style={style} className={className}>
      <div>
        <Typography variant='h3' mb={1}>Branch-specific coverage plan</Typography>
        <Typography variant='body2'>This Branch-specific research is not linked to any client contracts.</Typography>
      </div>
      <EditCoveragePlan
        value={currentValue}
        stateId={stateId}
        onChange={handleChange}
        stateName={election?.stateName}
      />
      {submittable && (
        <Section style={{ marginTop: '36px' }}>
          <Button variant='contained' color='primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Section>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export { EditBranchCoveragePlan };
